import React, { useState, useCallback } from 'react';
import { AlertCircle, CheckCircle, User, Mail, Lock } from 'lucide-react';
import DynamicProductRows from './DynamicProductRows';

// Move LoginForm component OUTSIDE to prevent recreation
const LoginForm = ({ formData, handleInputChange, handleLogin, loading, error }) => (
  <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
      <div className="text-center mb-8">
        <div className="bg-indigo-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
          <User className="w-8 h-8 text-indigo-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900">Welcome Back</h1>
        <p className="text-gray-600 mt-2">Please sign in to your account</p>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              key="email-input-stable"
              inputMode="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              autoComplete="email"
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent touch-manipulation"
              placeholder="Enter your email"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Password
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              key="password-input-stable"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              autoComplete="current-password"
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent touch-manipulation"
              placeholder="Enter your password"
              required
            />
          </div>
        </div>

        {error && (
          <div className="flex items-center space-x-2 text-red-600 bg-red-50 p-3 rounded-lg">
            <AlertCircle className="w-5 h-5" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <button
          onClick={handleLogin}
          disabled={loading}
          className="w-full bg-indigo-600 text-white py-3 rounded-lg font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Signing In...
            </div>
          ) : (
            'Sign In'
          )}
        </button>
      </div>

      <div className="mt-8 p-4 bg-gray-50 rounded-lg">
        <p className="text-sm text-gray-600 font-medium mb-2">Demo Users:</p>
        <div className="space-y-1 text-xs text-gray-500">
          {/* <div>ID: user001, Email: john@example.com, Pass: password123</div>
          <div>ID: user002, Email: sarah@example.com, Pass: mypassword</div> */}
          <div>ID: admin001, Email: admin@example.com, Pass: adminpass</div>
        </div>
      </div>
    </div>
  </div>
);

const LoginSystem = () => {
  // User database
  const users = [
    { id: 'user001', email: 'john@example.com', password: 'password123' },
    { id: 'user002', email: 'sarah@example.com', password: 'mypassword' },
    { id: 'user003', email: 'mike@example.com', password: 'securepass' },
    { id: '122', email: 'admin@example.com', password: 'adminpass' },
    { id: 'demo001', email: 'demo@example.com', password: 'demo123' }
  ];

  const [currentPage, setCurrentPage] = useState('login');
  const [formData, setFormData] = useState({
    userId: '',
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [authenticatedUser, setAuthenticatedUser] = useState(null);

  // Make handleInputChange stable with useCallback
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  }, []);

  const validateUser = useCallback(() => {
    const user = users.find(u => 
      u.email === formData.email && 
      u.password === formData.password
    );
    return user;
  }, [formData.email, formData.password]);

  const handleLogin = useCallback(() => {
    setError('');
    setLoading(true);

    // Simulate API call delay
    setTimeout(() => {
      const user = validateUser();
      
      if (user) {
        setAuthenticatedUser(user);
        setCurrentPage('productform');
      } else {
        setError('Invalid credentials. Please check your Email and Password.');
      }
      setLoading(false);
    }, 1000);
  }, [validateUser]);

  const handleLogout = useCallback(() => {
    setAuthenticatedUser(null);
    setCurrentPage('login');
    setFormData({ userId: '', email: '', password: '' });
    setError('');
  }, []);

  const ProductForm = () => (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <CheckCircle className="w-8 h-8 text-green-500 mr-3" />
              <h1 className="text-xl font-semibold text-gray-900">Product Management</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                Welcome, {authenticatedUser?.email}
              </span>
              <button
                onClick={handleLogout}
                className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-center py-12">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Login Successful!
            </h2>
            <p className="text-gray-600 mb-6">
              You have been successfully authenticated and redirected to the Product Form.
            </p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-w-md mx-auto">
              <div className="text-sm text-gray-700">
                <p><strong>User ID:</strong> {authenticatedUser?.id}</p>
                <p><strong>Email:</strong> {authenticatedUser?.email}</p>
                <p><strong>Status:</strong> <span className="text-green-600">Authenticated</span></p>
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-4">
              Product form component would be implemented here as requested.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const DynamicProductForm = () => (
    <DynamicProductRows 
      userId={authenticatedUser?.id} 
      onLogout={handleLogout}
      userInfo={authenticatedUser}
    />
  );

  // Use the external LoginForm component
  if (currentPage === 'login') {
    return (
      <LoginForm
        formData={formData}
        handleInputChange={handleInputChange}
        handleLogin={handleLogin}
        loading={loading}
        error={error}
      />
    );
  }

  return <DynamicProductForm />;
};

export default LoginSystem;